import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {

    catchError, concatMap, delay, filter, first, map, mergeMap, pluck, switchMap, take, tap,
} from 'rxjs/operators';

import { LoadModule, SetCurrentDate } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue, getContext, getCurrentDate, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { RequestParam } from '@core/services/http-requests/http-requests.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { cmsStatusPage } from '@features/cms-render/domain/constants/cms-render.constants';
import {
    CheckLinkType,
    CmsExternalEvent,
    CmsStatusSlug,
    ForceReloadCmsPages,
    LoadNewsletterModule,
    LoadPieceFormModule,
    LoadPieceProductCarouselModule,
    LoadPreviewSlug,
    LoadSlug,
    LoadSlugError,
    LoadSlugSuccess,
    LoadSlugTreeSuccess,
    LoadStoreProducts,
    RemoveAllSlugs,
    SectionFocused,
} from '@features/cms-render/domain/state/cms-render.actions';
import { getCmsPages } from '@features/cms-render/infrastructure/state/cms-render.selectors';
import { CmsRenderService } from '@features/cms-render/ui/services/cms-render/cms-render.service';
import { ProductsArrayFeaturePublicApi } from '@features/productsArray';
import { DuttiDateFormat, DuttiDate } from '@frontend/common/duttidate';
import { deepClone } from '@frontend/common/utils';
import { LoadEventError } from '@shared/analytics/store/analytics.actions';
import { getAnalyticsEvents } from '@shared/analytics/store/analytics.selectors';
import { ClickOnCmsComponentAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import {
    DebugBarChangeTimeTravellerDataSuccess,
    DebugBarChangeUserTypeSuccess,
} from '@shared/debugbar/store/debugbar.actions';
import { getGoogleServicesGtmEnabled } from '@shared/google-services/store/google-services.selectors';
import { SetHeaderTheme } from '@shared/header/store/header.actions';
import { GetConfig } from '@shared/navigation/store/navigation.actions';
import { NavigationService } from '@shared/services/navigation/navigation.service';
import { UpdateUserIsVip, selectUserIsVip } from '@shared/user/store';

@Injectable()
export class CmsRenderEffects {
    private readonly reloadCmsPageDelay = 200;

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        private readonly actions$: Actions,
        public store: Store,
        private readonly cmsRenderService: CmsRenderService,
        private readonly navigationService: NavigationService,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
        private readonly platform: PlatformService,
    ) { }

    public navigateBySectionConfig$: Observable<any> = this.store.select(getCmsConfigValue('navigateBySectionConfig'));

    public loadSlug$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlug),
            concatMap(action => {
                return this.store.select(selectUserIsVip).pipe(
                    // si es ssr no tenemos la store del usuario por eso cargamos como false el isVip
                    // TEMPORALMENTE SE TENDRA QUE IMPORTAR EL CORE DE EL USUARIO Y LA NEWSLETTER PARA OBTENER
                    // LOS DATOS SI ES VIP O NO
                    map((isVip) => {
                        return !this.platform.isBrowser() ? false : isVip ?? false;
                    }),
                    filter(isVip => isVip !== undefined && isVip !== null && typeof isVip === 'boolean'),
                    take(1),
                    map(isVip => ({
                        action,
                        isVip,
                    }))
                );
            }),
            concatLatestFrom(() => [
                this.store.select(getCmsPages),
                this.store.select(getStoreAndLanguage),
                this.store.select(getCurrentDate).pipe(
                    map(date => date.format(DuttiDateFormat.ISO_NO_UTC))
                ),
            ]),
            concatMap(([
                { action, isVip },
                contentArray,
                storeAndLanguage,
                date,
            ]) => {
                const { countryCode, languageCode } = storeAndLanguage;

                const content: any = contentArray[action.slug];

                // CONTENT ALEADY LOADED..
                if (content && content?.isVip === isVip) return EMPTY;

                const requestParams: RequestParam[] = this.cmsRenderService.setRequestParams(isVip, date);
                return this.cmsRenderService.getPage(action.slug, countryCode, languageCode, requestParams).pipe(
                    map((response) => {
                        const parsedContent = this.cmsRenderService.parseResponseContent(response.content);
                        return {
                            content: {
                                slug: response.slug,
                                content: parsedContent,
                                isVip,
                                tt: date,
                                versionId: response.versionId,
                                segmentId: response.segmentId,
                            },
                        };
                    }),
                    catchError((error: any) => of({ error: error, slug: action.slug }))
                );
            }),
            map((data: any) => {
                if (data.hasOwnProperty('error')) return LoadSlugError({ error: data.error, slug: data.slug });
                return LoadSlugSuccess(data);
            })
        );
    });

    public changeTimeTravellerOnPreview$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadPreviewSlug),
            map(action => {
                const url = action.url;
                return new URLSearchParams(url).get('timeTravelTo');
            }),
            filter(timeTraveller => !!timeTraveller),
            map(timeTraveller => {
                const date = new DuttiDate(timeTraveller as DuttiDateFormat.ISO_NO_UTC, DuttiDateFormat.ISO_NO_UTC);
                return SetCurrentDate({ currentDate: date });
            })
        );
    });

    // Efecto para cargar paginas en el preview del CMS dentro de un iframe
    public loadPreviewSlug$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadPreviewSlug),
            switchMap((action) => {
                return this.cmsRenderService.previewPage(action.url).pipe(
                    map((response) => {
                        this.cmsRenderService.sendSegmentToParent(response.segmentId);
                        const parsedContent = this.cmsRenderService.parseResponseContent(response.content);
                        const timeTravelTo = new URLSearchParams(action.url).get('timeTravelTo');
                        return {
                            content: {
                                slug: action.slug, content: parsedContent, isVip: action.isVip, tt: timeTravelTo,
                            },
                        };
                    }),
                    catchError((error: any) => of({ error: error, slug: action.slug }))
                );
            }),
            map((data: any) => {
                if (data.hasOwnProperty('error')) return LoadSlugError({ error: data.error, slug: data.slug });
                return LoadSlugSuccess(data);
            })
        );
    });

    public rebuildContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugSuccess),
            concatLatestFrom(() => this.store.select(getContext)),
            map(([data, context]) => {
                const {
                    slug, content, isVip, tt,
                } = data.content;
                const layout = content['1'].definitionId;
                const _content = this.cmsRenderService.preRenderActions(content, context, slug);
                const parentNode: any = deepClone(_content['1']);
                const rebuilded = this.cmsRenderService.rebuildContent(parentNode, _content, slug, layout);
                return LoadSlugTreeSuccess({
                    slug,
                    content: rebuilded,
                    isVip,
                    tt,
                    versionId: (data.content as any).versionId,
                    segmentId: (data.content as any).segmentId,
                });
            })
        );
    });

    /*
    * Possible states for the render according to its page
    * */

    public setLoadingStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlug),
            map(action => {
                return CmsStatusSlug({ slug: action.slug, status: cmsStatusPage.LOADING });
            })
        );
    });

    public setSuccessStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugSuccess),
            map(action => {
                return CmsStatusSlug({ slug: action.content.slug, status: cmsStatusPage.SUCCESS });
            })
        );
    });

    public setFailedStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugError),
            map(action => {
                return CmsStatusSlug({ slug: action.slug, status: cmsStatusPage.FAILED });
            })
        );
    });

    public setCmsConfig$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugSuccess),
            map(_ => {
                return GetConfig();
            })
        );
    });

    public setStoreProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugSuccess),
            map(action => this.cmsRenderService.getResponseProductsId(action.content.content)),
            filter(productsIds => productsIds.length > 0),
            switchMap(productsIds => {
                return this.productsArrayPublicApi.getProductsArrayWithoutCategory$(productsIds.join(',')); // TODO ECOMDUTI-20989 Recuperar los productos de la STORE en vez de la API en caso de tenerlos ya cargados
            }),
            map(productsData => {
                return LoadStoreProducts({ products: productsData });
            })
        );
    });

    /*
    * Reload slugs ( delete all and require )
    * */
    public reloadSlugsOnTimeTravellerChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DebugBarChangeTimeTravellerDataSuccess),
            delay(this.reloadCmsPageDelay),
            map(() => ForceReloadCmsPages()),
        );
    });

    /*
    * Reload isVipChange slugs ( delete all and require )
    * */
    public reloadSlugsOnIsVipChangeDebugger$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DebugBarChangeUserTypeSuccess),
            delay(this.reloadCmsPageDelay),
            map(() => ForceReloadCmsPages()),
        );
    });

    public reloadSlugsOnIsVipChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateUserIsVip),
            concatLatestFrom(() => [
                this.store.select(selectUserIsVip),
                this.store.select(getCmsPages),
            ]),
            filter(([_, isVip, cmsPages]) => {
                const keyPages = Object.keys(cmsPages ?? {});
                const cmsPagesArray = keyPages.map(key => cmsPages[key]);
                const contentCmsPagesIsDifferentUserFlag = cmsPagesArray.some((cmsPage) => cmsPage?.isVip !== isVip);
                return contentCmsPagesIsDifferentUserFlag;
            }),
            map(() => ForceReloadCmsPages())
        );
    });

    public sendPageViewAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSlugSuccess),
            // eslint-disable-next-line deprecation/deprecation
            pluck('content', 'slug'),
            filter((slug: string) => this.cmsRenderService.checkSlugForPageView(slug)),
            map((slug: string) => {
                return this.cmsRenderService.getPageViewEventBySlug(slug);
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error: unknown) => {
                console.error(error);
                return of(LoadEventError());
            })
        );
    });

    public checkLinkType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CheckLinkType),
            concatLatestFrom(() => [this.navigationService.baseUrl$]),
            map(([action, baseUrl]) => {
                this.cmsRenderService.checkLinkUrl(action, baseUrl);
            })
        );
    }, { dispatch: false });

    /**
     * Efecto para cargar el módulo del la pieza product-carousel
     */
    public loadPieceProductCarouselModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadPieceProductCarouselModule),
            map(() => {
                return LoadModule({
                    module: 'PieceProductCarouselModule',
                    path: () => import('@features/cms-render/ui/render-pieces/piece-product-carousel/piece-product-carousel.module').then(m => m.PieceProductCarouselModule),
                });
            })
        );
    });

    public loadNewsletterModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNewsletterModule),
            map(() =>
                LoadModule({
                    module: 'NewsletterFeatureUiModule',
                    path: () => import('@features/newsletter/newsletter-feature-ui.module').then(m => m.NewsletterFeatureUiModule),
                }))
        );
    });

    public loadPieceFormModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadPieceFormModule),
            map(() =>
                LoadModule({
                    module: 'PieceFormModule',
                    path: () => import('@features/cms-render/ui/render-pieces/piece-form/piece-form.module').then(m => m.PieceFormModule),
                }))
        );
    });

    public cmsExternalEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CmsExternalEvent),
            map(({
                categoryId, linkType, id, linkSrc, parent,
            }) => {
                return ClickOnCmsComponentAnalytics({
                    component: 'external_module', categoryId, linkType, id, linkSrc, parent,
                });
            })
        );
    });

    // Si GTM esta habilitado tiene que esperar que el hit
    // se haya enviado, luego procedemos con la navegacion
    public checkIfAnalyticsActiveAndNavigate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CmsExternalEvent),
            concatLatestFrom(() => this.store.select(getGoogleServicesGtmEnabled)),
            filter(([{ linkSrc }, gtmEnabled]) => gtmEnabled && !linkSrc.includes('customEvent')),
            map(([data, _]) => data),
            switchMap((data) => {
                return this.store
                    .select(getAnalyticsEvents)
                    .pipe(
                        map((events) => this.getLastHomeExternalEvent(events)),
                        filter((val) => !!val),
                        first(),
                        map(() => data)
                    );
            }),
            map(({
                linkSrc, newWindow, relativeLink,
            }) => {
                return CheckLinkType({
                    url: linkSrc, newWindow: newWindow, reload: false, relativeLink: relativeLink,
                });
            })
        );
    });

    private getLastHomeExternalEvent(events) {
        if (!events?.length) {
            return;
        }
        const homeEvents = events.filter((x) => x.type === 'ClickOnHomeComponent');
        const lastEvent = homeEvents[homeEvents.length - 1];
        if (lastEvent?.dataObject?.linkType === 'external' && lastEvent?.dataObject?.component === 'external_module') {
            return lastEvent;
        }
    }

    // Si GTM NO esta habilitado procedemos directamente con la navegacion
    public checkIfAnalyticsInactiveAndNavigate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CmsExternalEvent),
            concatLatestFrom(() => this.store.select(getGoogleServicesGtmEnabled)),
            filter(([{ linkSrc }, gtmEnabled]) => !gtmEnabled && !linkSrc.includes('customEvent')),
            map(([data, _]) => data),
            map(({
                linkSrc, newWindow, relativeLink,
            }) => {
                return CheckLinkType({
                    url: linkSrc, newWindow: newWindow, reload: false, relativeLink: relativeLink,
                });
            })
        );
    });

    /*
    * Force Reload All CMS Pages
    * */
    public reloadAllCmsPages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ForceReloadCmsPages),
            concatLatestFrom(() => this.store.select(getCmsPages)),
            map(([_, content]) => Object.keys(content)),
            tap(() => {
                this.store.dispatch(RemoveAllSlugs());
            }),
            delay(this.reloadCmsPageDelay),
            mergeMap((slugs: string[]) => {
                const actions = [];
                for (const slug of slugs) {
                    actions.push(LoadSlug({ slug }));
                }
                return actions;
            })
        );
    });

    public changeHeaderThemeByCmsSection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SectionFocused),
            filter((_) => this.platform.isBrowser()),
            map(({ theme }) => {
                return SetHeaderTheme({ theme: theme as any });
            })
        );
    });
}
